import type { AssignmentEtaDto, AssignmentLifecycleStatus } from '../../types/access'

/**
 * Read-only presentation of the backend-derived assignment lifecycle
 * (server/src/services/assignmentLifecycle.ts). Renders nothing it was not
 * given: no client-side ETA is ever computed or invented here.
 */

type Translate = (key: string, params?: Record<string, string | number>) => string

const LABELS: Record<AssignmentLifecycleStatus, string> = {
  scheduled: 'Scheduled',
  on_the_way: 'On My Way',
  arrived: 'Arrived',
  punched_in: 'Punched In',
  on_break: 'On Break',
  completed: 'Completed'
}

const PILL_CLASS: Record<AssignmentLifecycleStatus, string> = {
  scheduled: 'punched_out',
  on_the_way: 'on_the_way',
  arrived: 'arrived',
  punched_in: 'punched_in',
  on_break: 'break',
  completed: 'completed'
}

const EXCEPTION_LABELS: Record<string, string> = {
  late: 'Late',
  running_late: 'Running late',
  cannot_attend: 'Cannot attend',
  no_show: 'No show',
  missed_punch_out: 'Missed punch out'
}

const identity: Translate = (key, params) => key.replace(/\{(\w+)\}/g, (_, name) => String(params?.[name] ?? ''))

const formatClock = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const isAssignmentLifecycleStatus = (value: unknown): value is AssignmentLifecycleStatus =>
  typeof value === 'string' && value in LABELS

export function etaText(eta: AssignmentEtaDto | null | undefined, t: Translate = identity): string {
  if (!eta || eta.status === 'not_applicable') return ''
  if (eta.status === 'expired') return t('ETA expired')
  if (eta.status !== 'available' || eta.minutes == null) return t('ETA unavailable')
  const source = eta.source === 'worker_reported' ? t('worker-reported') : t('straight-line estimate')
  const arrival = formatClock(eta.arrivalAt)
  return eta.minutes <= 0
    ? t('Arriving now ({source})', { source })
    : t('ETA {minutes} min · {arrival} ({source})', { minutes: eta.minutes, arrival, source })
}

export default function AssignmentLifecycleBadge({
  status,
  exception,
  eta,
  updatedAt,
  t = identity,
  testId
}: {
  status?: string | null
  exception?: string | null
  eta?: AssignmentEtaDto | null
  updatedAt?: string | null
  t?: Translate
  testId?: string
}) {
  if (!isAssignmentLifecycleStatus(status)) return <span className="helper">{t('Not available')}</span>
  const etaLine = etaText(eta, t)
  const updated = formatClock(updatedAt)
  return (
    <span className="lifecycle-badge" data-testid={testId} data-lifecycle-status={status}>
      <span className="lifecycle-badge__row">
        <span className={`status-pill status-pill--${PILL_CLASS[status]}`}>{t(LABELS[status])}</span>
        {exception && EXCEPTION_LABELS[exception] ? <span className="status-pill status-pill--late">{t(EXCEPTION_LABELS[exception])}</span> : null}
      </span>
      {etaLine ? <span className="helper" data-testid={testId ? `${testId}-eta` : undefined}>{etaLine}</span> : null}
      {updated ? <span className="helper">{t('Updated {time}', { time: updated })}</span> : null}
    </span>
  )
}
